import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { TwoFactorSetupPanel, RecoveryCodesPanel, errorText } from './TwoFactorPanels';
import HelpIcon from './HelpIcon';
import HelpPane from './HelpPane';
import { getHelpContent } from './helpContent';

// Settings > Two-step sign-in: a code from an authenticator app after the password. Anyone can turn it on for themselves;
// an Org Admin can require it of the whole organization (Org settings), which also makes it impossible to turn off here.

function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState('idle'); // idle | setup | codes | regenerate | disable
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const help = getHelpContent('two-step-sign-in');

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/two-factor/status');
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeDialog = () => { setMode('idle'); setCode(''); setPassword(''); };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post('/api/two-factor/recovery-codes', { code });
      setCodes(res.data.recoveryCodes);
      setMode('codes');
      setCode('');
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data, 'Could not make new codes'));
    }
    setBusy(false);
  };

  const turnOff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post('/api/two-factor/disable', { password, code });
      toast.success('Two-step sign-in is off');
      closeDialog();
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data, 'Could not turn it off'));
    }
    setBusy(false);
  };

  const forgetBrowsers = async () => {
    try {
      await axios.delete('/api/two-factor/browsers');
      toast.success('Every browser will ask for the code again');
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data, 'Could not do that'));
    }
  };

  if (!status) return null;

  return (
    <Card className="mb-4" data-testid="two-factor-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Two-step sign-in{help && <HelpIcon onClick={() => setShowHelp(true)} />}</h5>
        {status.enabled ? <Badge bg="success">On</Badge> : <Badge bg="secondary">Off</Badge>}
      </Card.Header>
      <Card.Body>
        {!status.enabled && (
          <>
            <p className="text-muted small">
              Asks for a 6-digit code from an authenticator app on your phone, as well as your password. It keeps your
              business books safe even if your password is guessed or leaked.
            </p>
            {status.requiredByOrganization && (
              <Alert variant="warning" className="py-2 small">Your organization requires this. Set it up now, or you will be asked at your next sign-in.</Alert>
            )}
            <Button variant="primary" onClick={() => setMode('setup')} data-testid="two-factor-turn-on">Turn on</Button>
          </>
        )}
        {status.enabled && (
          <>
            <p className="small mb-2">
              You are asked for a code from your authenticator app when you sign in.
              {' '}You have <strong>{status.recoveryCodesLeft}</strong> recovery code{status.recoveryCodesLeft === 1 ? '' : 's'} left
              {status.recoveryCodesLeft <= 2 && <span className="text-danger"> (make new ones soon)</span>}.
            </p>
            {status.trustedBrowsers > 0 && (
              <p className="small text-muted mb-2">
                {status.trustedBrowsers} browser{status.trustedBrowsers === 1 ? ' is' : 's are'} remembered, so no code is asked there.{' '}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={forgetBrowsers}>Forget them</Button>
              </p>
            )}
            <div className="d-flex gap-2 flex-wrap mt-3">
              <Button variant="outline-secondary" size="sm" onClick={() => setMode('regenerate')} data-testid="two-factor-new-codes">New recovery codes</Button>
              {!status.requiredByOrganization && (
                <Button variant="outline-danger" size="sm" onClick={() => setMode('disable')} data-testid="two-factor-turn-off">Turn off</Button>
              )}
            </div>
            {status.requiredByOrganization && (
              <p className="small text-muted mt-3 mb-0">Your organization requires two-step sign-in, so it can't be turned off.</p>
            )}
          </>
        )}
      </Card.Body>

      <Modal show={mode === 'setup'} onHide={closeDialog} centered>
        <Modal.Header closeButton><Modal.Title>Turn on two-step sign-in</Modal.Title></Modal.Header>
        <Modal.Body>
          <TwoFactorSetupPanel
            start={async () => {
              try {
                return (await axios.post('/api/two-factor/setup/start')).data;
              } catch (e) {
                return { error: errorText(e.response?.data, 'Could not start') };
              }
            }}
            confirm={async (c) => {
              try {
                const res = await axios.post('/api/two-factor/setup/confirm', { code: c });
                setCodes(res.data.recoveryCodes);
                setMode('codes');
                load();
                return null;
              } catch (e) {
                return errorText(e.response?.data, 'That code is not right');
              }
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={mode === 'codes'} backdrop="static" keyboard={false} centered>
        <Modal.Header><Modal.Title>Your recovery codes</Modal.Title></Modal.Header>
        <Modal.Body>
          <RecoveryCodesPanel codes={codes} doneLabel="Done" onDone={() => { setCodes([]); closeDialog(); }} />
        </Modal.Body>
      </Modal>

      <Modal show={mode === 'regenerate'} onHide={closeDialog} centered>
        <Form onSubmit={regenerate}>
          <Modal.Header closeButton><Modal.Title>New recovery codes</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">Your old recovery codes stop working. Type the code your app shows now to continue.</p>
            <Form.Control type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus required data-testid="two-factor-regenerate-code" />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy || !code.trim()}>Make new codes</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={mode === 'disable'} onHide={closeDialog} centered>
        <Form onSubmit={turnOff}>
          <Modal.Header closeButton><Modal.Title>Turn off two-step sign-in</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Your password</Form.Label>
              <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required data-testid="two-factor-disable-password" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Code from your app (or a recovery code)</Form.Label>
              <Form.Control type="text" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required data-testid="two-factor-disable-code" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={busy || !password || !code.trim()}>Turn off</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      {help && <HelpPane show={showHelp} onHide={() => setShowHelp(false)} title={help.title}>{help.content}</HelpPane>}
    </Card>
  );
}

export default TwoFactorSettings;
